import type { ComponentAccessResponse, ComponentLockReason } from '../app/modules/types/componentAccess'

/**
 * Display-side view of `GET …/permissions/components/access/`.
 *
 * The backend stays the authority — everything here only decides what to grey
 * out. `known` is false while the answer is missing (loading, or a 503): access
 * is then UNKNOWN, never allowed, so nothing may be added or run on its strength.
 */
export interface ComponentAccessView {
  known: boolean
  /** `null` when usable (or unknown); otherwise why it is locked. */
  lockOf: (componentName: string) => ComponentLockReason | null
}

/** The start node is never locked and never listed. */
export const START_COMPONENT_NAME = '_input'

export function buildAccessView(data: ComponentAccessResponse | undefined): ComponentAccessView {
  if (!data) return { known: false, lockOf: () => null }
  if (data.all) return { known: true, lockOf: () => null }
  const byName = new Map((data.components ?? []).map((c) => [c.name, c]))
  return {
    known: true,
    lockOf: (name) => {
      if (name === START_COMPONENT_NAME) return null
      const entry = byName.get(name)
      // A component the list does not mention is not one the user can use.
      if (!entry) return 'role'
      return entry.can_use ? null : entry.locked_reason ?? 'role'
    },
  }
}

/** Component names (deduped, in order) among `names` that the user may not use. */
export function lockedComponentNames(
  names: Array<string | null | undefined>,
  view: ComponentAccessView,
): string[] {
  const out: string[] = []
  for (const name of names) {
    if (!name || name === START_COMPONENT_NAME || out.includes(name)) continue
    if (view.lockOf(name)) out.push(name)
  }
  return out
}

type ErrorBody = { code?: string; components?: unknown; details?: { components?: unknown }; errors?: unknown }

const bodyOf = (error: unknown): ErrorBody | undefined =>
  (error as { response?: { data?: unknown } })?.response?.data as ErrorBody | undefined

/** `components` carried by the scan 403 / `details.components` of a run 403, else []. */
export function refusedComponents(error: unknown): string[] {
  const body = bodyOf(error)
  const list = body?.components ?? body?.details?.components
  return Array.isArray(list) ? list.filter((c): c is string => typeof c === 'string') : []
}

export type ComponentRefusal =
  | { kind: 'not_permitted'; components: string[] }
  | { kind: 'unverified' }
  | { kind: 'other' }

/** Classify a scan / schedule / run failure by status, never by message text. */
export function classifyComponentRefusal(error: unknown): ComponentRefusal {
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status === 403 && bodyOf(error)?.code === 'COMPONENT_NOT_PERMITTED') {
    return { kind: 'not_permitted', components: refusedComponents(error) }
  }
  if (status === 503) return { kind: 'unverified' }
  return { kind: 'other' }
}

/** Graph-save 400 entries that name a locked node: `[{id, code, message}]` → the node ids. */
export function notPermittedNodeIds(error: unknown): string[] {
  if ((error as { response?: { status?: number } })?.response?.status !== 400) return []
  const errors = bodyOf(error)?.errors
  if (!Array.isArray(errors)) return []
  return errors
    .filter((e) => e && e.code === 'COMPONENT_NOT_PERMITTED' && typeof e.id === 'string')
    .map((e) => e.id as string)
}
